import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { Reveal, useInView } from './Reveal'
import { useCounter, useParallax, prefersReducedMotion } from '../../lib/motion'
import { CoinMark } from '../Logo'

/** Swap the `w=` param on an Unsplash URL — one master URL, responsive variants. */
const srcAt = (src: string, w: number) => src.replace(/([?&])w=\d+/, `$1w=${w}`)

/**
 * Full-screen cinematic slideshow (A24-style): slow crossfade through set photography
 * with an alternating Ken Burns drift and a legibility scrim. Static under reduced motion.
 *
 * Perf contract: only the FIRST slide is fetched at boot (fetchpriority=high, responsive
 * srcset); later slides mount just-in-time, and Ken Burns runs only on the two layers
 * taking part in the current crossfade — never on hidden slides.
 */
export function HeroSlideshow({
  slides,
  interval = 6000,
  play = true,
  onFirstFrame,
}: {
  slides: { src: string; alt: string }[]
  interval?: number
  /** Hold the rotation (not the first frame) until the boot veil lifts. */
  play?: boolean
  /** Fires once the first slide has pixels — drives the preloader. */
  onFirstFrame?: () => void
}) {
  const [[idx, prev], setCur] = useState<[number, number]>([0, -1])
  const [mounted, setMounted] = useState<number[]>([0])
  const reduced = prefersReducedMotion()

  useEffect(() => {
    if (reduced || slides.length < 2 || !play) return
    const t = setInterval(() => setCur(([i]) => [(i + 1) % slides.length, i]), interval)
    return () => clearInterval(t)
  }, [slides.length, interval, reduced, play])

  // Mount the upcoming slide just-in-time: ~1.5s after the veil lifts for the first
  // crossfade, immediately after each advance for the rest. Boot fetches ONE hero image.
  useEffect(() => {
    if (reduced || slides.length < 2 || !play) return
    const next = (idx + 1) % slides.length
    const t = setTimeout(
      () => setMounted((m) => (m.includes(next) ? m : [...m, next])),
      prev === -1 ? 1500 : 0,
    )
    return () => clearTimeout(t)
  }, [idx, prev, play, reduced, slides.length])

  // First-frame signal — the ref callback covers the cache-hit case where load already fired.
  const firstRef = useCallback(
    (el: HTMLImageElement | null) => {
      if (el?.complete && el.naturalWidth > 0) onFirstFrame?.()
    },
    [onFirstFrame],
  )

  return (
    <div className="absolute inset-0 overflow-hidden bg-ink" aria-hidden>
      {slides.map((s, i) => {
        if (!mounted.includes(i)) return null
        const active = i === idx || i === prev
        return (
          <img
            key={s.src}
            ref={i === 0 ? firstRef : undefined}
            src={srcAt(s.src, 1600)}
            srcSet={`${srcAt(s.src, 960)} 960w, ${srcAt(s.src, 1600)} 1600w, ${srcAt(s.src, 2400)} 2400w`}
            sizes="100vw"
            alt=""
            decoding="async"
            onLoad={i === 0 ? onFirstFrame : undefined}
            {...(i === 0 ? ({ fetchpriority: 'high' } as Record<string, string>) : undefined)}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-[1600ms] ease-in-out"
            style={{
              opacity: i === idx ? 1 : 0,
              visibility: active ? 'visible' : 'hidden',
              animation:
                !reduced && active ? `kenburns${i % 2 ? '-out' : '-in'} 14s ease-in-out infinite alternate` : undefined,
              willChange: active ? 'opacity, transform' : undefined,
            }}
          />
        )
      })}
      {/* Scrim: cinematic legibility without flattening the photo. */}
      <div className="absolute inset-0 bg-gradient-to-b from-ink/55 via-ink/15 to-ink/65" />
      {/* Slide counter, lower right (camille-style). Desktop only — it collides with the tagline on phones. */}
      <div className="absolute bottom-8 right-6 hidden items-center gap-3 md:right-10 lg:flex">
        <span className="font-display text-sm tracking-widest text-paper/85">
          {String(idx + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
        </span>
        <div className="flex gap-1.5">
          {slides.map((_, i) => (
            <span
              key={i}
              className={`h-px w-7 transition-colors duration-500 ${i === idx ? 'bg-paper' : 'bg-paper/30'}`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Full-bleed cinematic interlude (hero-grade): an edge-to-edge set photograph with a
 * slow parallax drift, a legibility scrim, and one masked editorial line — the same
 * photographic register as the hero, breaking up the editorial sections.
 */
export function CineBand({
  src,
  alt,
  eyebrow,
  lines,
}: {
  src: string
  alt: string
  eyebrow?: string
  lines: ReactNode[]
}) {
  const drift = useParallax<HTMLImageElement>(-0.12)
  return (
    <section className="relative h-[56vh] min-h-[380px] w-full overflow-hidden bg-ink md:h-[72vh]" aria-label={alt}>
      {/* Overscanned so the parallax drift never exposes edges. */}
      <img
        ref={drift}
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        className="absolute -top-[12%] left-0 h-[124%] w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/65 via-ink/15 to-ink/30" aria-hidden />
      <div className="txt-cine absolute inset-x-0 bottom-0">
        <div className="mx-auto max-w-[1320px] px-6 pb-10 text-paper md:px-10 md:pb-16">
          {eyebrow && <Reveal as="p" className="eyebrow !text-paper/75">{eyebrow}</Reveal>}
          <Lines lines={lines} className="text-display mt-3 font-display" />
        </div>
      </div>
    </section>
  )
}

/** Curtain photo reveal: clip wipes up on first view while the image settles from a slow zoom. */
export function Curtain({
  src,
  alt,
  className = '',
  imgClassName = '',
  parallax = 0,
  eager = false,
}: {
  src: string
  alt: string
  className?: string
  imgClassName?: string
  /** Optional drift speed (0.1–0.3) — the framed image floats against scroll. */
  parallax?: number
  eager?: boolean
}) {
  const { ref, inView } = useInView<HTMLDivElement>('0px 0px -8% 0px')
  const drift = useParallax<HTMLImageElement>(parallax)
  // The observed wrapper stays unclipped (Chromium folds the target's own clip-path into
  // IntersectionObserver geometry — a fully-clipped element never "intersects").
  return (
    <div ref={ref} className={`overflow-hidden bg-tint2 ${className}`}>
      <div className={`curtain h-full w-full ${inView ? 'is-open' : ''}`}>
        <img
          ref={parallax ? drift : undefined}
          src={src}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          className={`h-full w-full object-cover ${imgClassName}`}
        />
      </div>
    </div>
  )
}

/** Display headline revealed line by line through masks (pass each line as a string). */
export function Lines({
  lines,
  className = '',
  lineClassName = '',
  as: As = 'h2',
  stagger = 110,
  play = true,
}: {
  lines: ReactNode[]
  className?: string
  lineClassName?: string
  as?: 'h1' | 'h2' | 'p'
  stagger?: number
  /** Hold the line reveal until true (e.g. while the boot veil is still up). */
  play?: boolean
}) {
  const { ref, inView } = useInView<HTMLDivElement>()
  return (
    <div ref={ref} className={inView && play ? 'lines-in' : ''}>
      <As className={className}>
        {lines.map((l, i) => (
          <span key={i} className="line-mask">
            <span style={{ transitionDelay: `${i * stagger}ms` }} className={lineClassName}>{l}</span>
          </span>
        ))}
      </As>
    </div>
  )
}

/** A tinted content block that drifts gently against scroll (the "moving blocks"). */
export function DriftBlock({
  speed = 0.12,
  className = '',
  style,
  children,
}: {
  speed?: number
  className?: string
  style?: CSSProperties
  children?: ReactNode
}) {
  const ref = useParallax<HTMLDivElement>(speed)
  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  )
}

/** Big number that counts up when it enters the viewport. */
export function StatCounter({
  value,
  suffix = '',
  prefix = '',
  label,
  className = '',
}: {
  value: number
  suffix?: string
  prefix?: string
  label: string
  className?: string
}) {
  const { ref, inView } = useInView<HTMLDivElement>()
  const n = useCounter(value, inView)
  return (
    <div ref={ref} className={className}>
      <div className="stat-num font-display">{prefix}{n}{suffix}</div>
      <div className="eyebrow mt-2.5">{label}</div>
    </div>
  )
}

/** Soft blurred organic shapes drifting behind a section (the Floema backdrop). */
export function BlobField({ tone = 'green' }: { tone?: 'green' | 'cool' }) {
  const blobs =
    tone === 'green'
      ? [
          { c: 'rgb(177 219 184 / 0.55)', s: '34vw', t: '6%', l: '8%', d: '0s' },
          { c: 'rgb(225 244 223 / 0.8)', s: '40vw', t: '32%', l: '58%', d: '-8s' },
          { c: 'rgb(182 206 213 / 0.4)', s: '26vw', t: '58%', l: '22%', d: '-15s' },
        ]
      : [
          { c: 'rgb(182 206 213 / 0.5)', s: '32vw', t: '10%', l: '60%', d: '0s' },
          { c: 'rgb(207 231 211 / 0.6)', s: '36vw', t: '46%', l: '6%', d: '-11s' },
        ]
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {blobs.map((b, i) => (
        <span
          key={i}
          className="blob"
          style={{ background: b.c, width: b.s, height: b.s, top: b.t, left: b.l, animationDelay: b.d }}
        />
      ))}
    </div>
  )
}

/** Gliding photo strip (pauses on hover). Items repeat once for the seamless loop. */
export function GalleryMarquee({ photos }: { photos: { src: string; alt: string }[] }) {
  const row = [...photos, ...photos]
  return (
    <div className="marquee-mask overflow-hidden">
      <div className="gallery-track py-2">
        {row.map((p, i) => (
          <div key={i} className="h-56 w-80 shrink-0 overflow-hidden rounded-card md:h-72 md:w-[26rem]">
            {/* Tiles render ≤416px wide — fetch ~2x that, not the master 1600–2400px asset. */}
            <img src={srcAt(p.src, 900)} alt={i < photos.length ? p.alt : ''} loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Soft boot veil: bone screen, coin mark, a thin filling line. Holds while the hero's
 * first frame + display face load (Landing decides `done`), lifts, then unmounts.
 */
export function Preloader({ done }: { done: boolean }) {
  const [gone, setGone] = useState(false)
  useEffect(() => {
    if (!done) return
    const t = setTimeout(() => setGone(true), 750) // matches the CSS fade
    return () => clearTimeout(t)
  }, [done])
  if (gone) return null
  return (
    <div className={`preloader ${done ? 'is-done' : ''}`} aria-hidden>
      <div className="text-center">
        <CoinMark className="mx-auto h-16 w-16 text-ink" />
        <div className="preloader-line mx-auto mt-7"><span /></div>
      </div>
    </div>
  )
}
